import { useCallback, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

import { Tooltip } from "./Tooltip";

export interface FixedTabSpec {
  id: string;
  label: string;
  to: string;
  active: boolean;
  icon: ReactNode;
  /**
   * When set, an initials avatar chip replaces `icon` — used by the Search tab
   * while it hosts an open member (see TabbedAppShell's SINGLE-MEMBER doc), so
   * the tab reads as that member without a separate member tab existing.
   */
  initials?: string;
  /**
   * When set, the tab renders a real close `<button>` that clears whatever the
   * tab is hosting and reverts it to its permanent identity. Permanent tabs
   * pass this ONLY while they host something closable — the tab itself never
   * goes away. Its presence also switches the tab's wrapper element (see
   * FixedTab below).
   */
  onClose?: () => void;
}

interface TabShellNavProps {
  fixedTabs: FixedTabSpec[];
}

const TAB_BASE_CLASS =
  "group relative flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium " +
  "transition-colors cursor-pointer select-none whitespace-nowrap shrink-0 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Nav tone. Inactive tabs sit at `text-foreground/80`, NOT
 * `text-muted-foreground`, which is what they used to share with the header's
 * theme/bell/avatar cluster — same colour token, and a 15px icon against those
 * utilities' 16px, so the two groups were indistinguishable by weight (the
 * "nav icons look too similar to the tools icons" report). Utilities keep
 * `text-muted-foreground`; navigation is content-weight because it answers
 * "where am I", which outranks an ambient toggle.
 *
 * Active still gets the `bg-accent` fill, so the ladder is
 * muted utility -> legible nav -> filled current tab.
 */
function tabToneClass(active: boolean): string {
  return active
    ? "bg-accent text-foreground"
    : "text-foreground/80 hover:text-foreground hover:bg-accent";
}

/**
 * P6d — responsive keep-alive tab bar. Renders ONLY permanent tabs (Search,
 * Practice Schedule, Priority Worklist): there is no member tab, because an
 * open member REUSES the Search tab's slot and relabels it (`initials` +
 * `label`) instead of adding a chip of its own.
 *
 * Icon-condensing for the narrow Chrome-extension side panel is driven
 * PURELY by CSS `@container` (see the `.tabshell-*` rules in index.css)
 * against THIS bar's own inline-size — not the viewport. Both the wide
 * (icon + label) and narrow (icon-only / avatar-initials-chip) markup are
 * ALWAYS present in the DOM; the container query only toggles which is
 * visible. No width-measuring JS, no new dependency: Tailwind v3 ships no
 * container-query variant here (the plugin isn't installed, and the repo
 * boundary forbids adding one for this task) — plain CSS `@container` is a
 * native platform feature, not a Tailwind add-on.
 *
 * Implements the ARIA "tabs" authoring pattern: `role="tablist"` wraps
 * `role="tab"` entries with roving `tabIndex` (only the active tab is
 * Tab-focusable; ArrowLeft/Right/Home/End move focus within the list, manual
 * activation via Enter/Space). Every entry carries its FULL label as
 * `aria-label` regardless of condensed state, and a portaled `Tooltip`
 * surfaces it on hover/focus.
 */
export function TabShellNav({ fixedTabs }: TabShellNavProps) {
  const navigate = useNavigate();

  const handleTabListKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") {
        return;
      }
      const tabs = Array.from(
        e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'),
      );
      if (tabs.length === 0) return;
      const currentIndex = tabs.indexOf(document.activeElement as HTMLElement);
      let nextIndex = currentIndex === -1 ? 0 : currentIndex;
      if (e.key === "ArrowRight") nextIndex = (nextIndex + 1) % tabs.length;
      else if (e.key === "ArrowLeft") nextIndex = (nextIndex - 1 + tabs.length) % tabs.length;
      else if (e.key === "Home") nextIndex = 0;
      else if (e.key === "End") nextIndex = tabs.length - 1;
      e.preventDefault();
      tabs[nextIndex]?.focus();
    },
    [],
  );

  // Since the Home and Search tabs were removed (TabbedAppShell's
  // `fixedTabs`), an ungated session with no member open can hand this an
  // EMPTY list. Render nothing rather than an empty `role="tablist"`, which
  // is invalid ARIA (a tablist owns one or more tabs) and would announce a
  // tab list with no tabs. AFTER the `useCallback` above on purpose: an early
  // return above a hook is a conditional hook call.
  if (fixedTabs.length === 0) return null;

  return (
    <div
      role="tablist"
      aria-label="Open tabs"
      onKeyDown={handleTabListKeyDown}
      className="tabshell-nav-container flex items-center gap-1 min-w-0 overflow-x-auto"
    >
      {fixedTabs.map((tab) => (
        <Tooltip key={tab.id} label={tab.label}>
          <FixedTab tab={tab} onActivate={() => navigate(tab.to)} />
        </Tooltip>
      ))}
    </div>
  );
}

/** The icon-or-initials leading glyph plus the (CSS-condensable) label text. */
function TabContent({ tab }: { tab: FixedTabSpec }) {
  const hostsMember = tab.initials !== undefined;
  return (
    <>
      {hostsMember ? (
        <span
          aria-hidden="true"
          className={
            "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold " +
            (tab.active
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground")
          }
        >
          {tab.initials}
        </span>
      ) : (
        <span aria-hidden="true" className="shrink-0 flex items-center">
          {tab.icon}
        </span>
      )}
      {/* A member name can be arbitrarily long — truncate that, but never the
          short permanent labels (which would needlessly clip e.g. "Priority
          Worklist"). */}
      <span
        className={
          hostsMember
            ? "tabshell-tab-label max-w-[10rem] truncate"
            : "tabshell-tab-label"
        }
      >
        {tab.label}
      </span>
    </>
  );
}

/**
 * One permanent tab. Without `onClose` it's a real `<a href>`, so middle-click
 * / open-in-new-tab keep working on plain nav tabs. WITH `onClose` it must
 * become a `<div>`: a `<button>` nested inside an `<a>` is invalid HTML
 * (interactive content inside an anchor) and browsers recover from it
 * unpredictably. Both branches keep the identical `role="tab"` + roving
 * tabIndex + Enter/Space contract, so the ARIA behavior doesn't change with
 * the wrapper.
 */
function FixedTab({ tab, onActivate }: { tab: FixedTabSpec; onActivate: () => void }) {
  const shared = {
    role: "tab",
    "aria-selected": tab.active,
    "aria-label": tab.label,
    tabIndex: tab.active ? 0 : -1,
  } as const;

  const onClose = tab.onClose;
  if (!onClose) {
    return (
      <a
        {...shared}
        href={tab.to}
        onClick={(e) => {
          e.preventDefault();
          onActivate();
        }}
        onKeyDown={(e) => {
          // Anchors activate on Enter natively; Space only scrolls by
          // default (no click), so wire it up explicitly to match the
          // ARIA tabs pattern's "Enter/Space activate" contract.
          if (e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        }}
        className={`${TAB_BASE_CLASS} ${tabToneClass(tab.active)}`}
      >
        <TabContent tab={tab} />
      </a>
    );
  }

  return (
    <div
      {...shared}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      className={`${TAB_BASE_CLASS} ${tabToneClass(tab.active)} pr-1.5`}
    >
      <TabContent tab={tab} />
      <button
        type="button"
        aria-label={`Close ${tab.label} tab`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground hover:bg-background/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X size={12} aria-hidden="true" />
      </button>
    </div>
  );
}
